import React, { useCallback, useMemo, useState } from 'react';
import { DvrOutlined, InfoOutlined, OpenInNewOutlined } from '@mui/icons-material';
import { useAppInsightsContext, useTrackEvent } from '@microsoft/applicationinsights-react-js';
import DashboardCard, { CardHeader } from '../DashboardCard/DashboardCard';
import configData from '../../../../config.json';
import { useTranslation } from 'react-i18next';

const TIFO_URL = configData.REACT_APP_TIFO_URL;

function lastOpenedKey(userId) {
  return `tifo_last_opened_${userId ?? 'anon'}`;
}

function formatLastOpened(iso, locale) {
  try {
    return new Intl.DateTimeFormat(locale, {
      day: 'numeric', month: 'short', hour: 'numeric', minute: '2-digit',
    }).format(new Date(iso));
  } catch {
    return null;
  }
}

export default function OnboardingCard({ portalUrl, userId, animDelay }) {
  const { t, i18n } = useTranslation();
  const locale = i18n?.language || 'en';

  const appInsights = useAppInsightsContext();
  const trackPortalOpen = useTrackEvent(appInsights, 'tifo portal opened', {});

  const [lastOpenedIso, setLastOpenedIso] = useState(() => {
    try {
      return localStorage.getItem(lastOpenedKey(userId)) || null;
    } catch {
      return null;
    }
  });

  const href = useMemo(() => {
    const base = portalUrl || TIFO_URL;
    if (!base) return null;
    try {
      const url = new URL(base);
      url.searchParams.set('lang', locale);
      url.searchParams.set('src', 'asd');
      return url.toString();
    } catch {
      return base;
    }
  }, [portalUrl, locale]);

  const handleOpen = useCallback(
    (source) => () => {
      trackPortalOpen({ props: { user: userId, source, locale } });
      try {
        const now = new Date().toISOString();
        localStorage.setItem(lastOpenedKey(userId), now);
        setLastOpenedIso(now);
      } catch {
        // storage unavailable — link still opens
      }
    },
    [userId, locale, trackPortalOpen]
  );

  const lastOpenedLabel = lastOpenedIso ? formatLastOpened(lastOpenedIso, locale) : null;

  if (!href) {
    return (
      <DashboardCard animDelay={animDelay}>
        <CardHeader icon={<DvrOutlined />} title={t('dashboard.cards.onboarding')} />
        <div className="px-5 pb-5">
          <p className="text-[14px] text-[#6B7A85] leading-relaxed m-0">
            {t('dashboard.onboarding.unavailable')}
          </p>
        </div>
      </DashboardCard>
    );
  }

  return (
    <DashboardCard>
      <CardHeader icon={<DvrOutlined />} title="Onboarding" />
      <div className="px-5 pb-5 flex flex-col gap-3">
        <div className="flex gap-3 bg-[#E6F4F8] rounded-[10px] p-4 border border-[#C5E4EE]">
          <InfoOutlined sx={{ fontSize: 17, color: '#0094B3', flexShrink: 0, marginTop: '1px' }} />
          <p className="text-[14px] text-[#1A2B35] leading-relaxed m-0">
            Your onboarding runs in a secure guided portal. Pick up exactly where
            you left off — progress saves automatically.
          </p>
        </div>
        <a
          href={portalUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center justify-center gap-2 w-full py-3 bg-[#0094B3] text-white text-[15px] font-semibold rounded-[8px] no-underline hover:bg-[#0081A0] transition-colors duration-150"
        >
          Open Onboarding Portal
          <OpenInNewOutlined sx={{ fontSize: 16 }} />
        </a>
      </div>
    </DashboardCard>
  );
}